import {Component, OnDestroy, OnInit, ChangeDetectionStrategy, afterNextRender, inject} from '@angular/core';
import {ArticleService} from '@services/article.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Article} from '@/app/core/models/article';
import {DomSanitizer, SafeHtml, Title} from '@angular/platform-browser';
import {environment} from '@/environments/environment';
import {ButtonSmallComponent} from '@/app/shared/components/buttons/button-small/button-small.component';
import {getImageByArticleType} from '../../core/utils/articleUtils';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LastVideosComponent} from '@components/last-videos/last-videos.component';
import {LocalizedDatePipe} from '@/app/shared/pipes/localizedDate.pipe';
import {TranslateDynamicService} from '@services/translate-dynamic.service';
import {Subscription} from 'rxjs';
import {SeoService} from '@/app/core/seo/seo.service';
import {JsonLdService} from '@/app/core/seo/json-ld.service';
import {buildArticleJsonLd, buildBreadcrumbJsonLd} from '@/app/core/seo/article.jsonld';
import {environment as env} from '@/environments/environment';

@Component({
	selector: 'app-news-details',
	imports: [
		ButtonSmallComponent,
		TranslatePipe,
		LastVideosComponent,
		LocalizedDatePipe,
	],
	templateUrl: './news-details.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./news-details.component.css']
})
export class NewsDetailsComponent implements OnInit, OnDestroy {

	private static readonly JSON_LD_ARTICLE = 'article';
	private static readonly JSON_LD_BREADCRUMB = 'article-breadcrumb';

	private readonly seo = inject(SeoService);
	private readonly jsonLd = inject(JsonLdService);

	constructor(
		private readonly articleService: ArticleService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly sanitizer: DomSanitizer,
		private readonly router: Router,
		private readonly titleService: Title,
		protected readonly translateDynamicService: TranslateDynamicService,
		private readonly translateService: TranslateService,
	) {
		// window n'existe pas pendant le rendu serveur.
		afterNextRender(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
	}

	minioBaseUrl = environment.minioBaseUrl;
	articleId: string | null = null;
	article: Article | null = null;
	previousArticle?: string;
	nextArticle?: string;
	safeHtml: SafeHtml = '';

	private langChangeSub!: Subscription;
	protected readonly getImageByArticleType = getImageByArticleType;

	ngOnInit(): void {
		this.langChangeSub = this.translateService.onLangChange.subscribe(event => {
			this.loadPageTitleAndArticleContent();
		})

		this.activatedRoute.paramMap.subscribe(paramMap => {
			this.articleId = paramMap.get('articleId');
			this.loadArticle()
		})
	}

	ngOnDestroy(): void {
		if(this.langChangeSub) {
			this.langChangeSub.unsubscribe();
		}
		// Sans ça, le balisage décrirait l'article précédent après une navigation SPA.
		this.jsonLd.remove(NewsDetailsComponent.JSON_LD_ARTICLE);
		this.jsonLd.remove(NewsDetailsComponent.JSON_LD_BREADCRUMB);
	}

	private loadArticle() {
		if (this.articleId) {
			this.articleService.getArticle(this.articleId).subscribe({
				next: (data) => {
					this.article = data.article;
					this.previousArticle = data.previousArticleId;
					this.nextArticle = data.nextArticleId;
					this.loadPageTitleAndArticleContent();
				},
				error: () => {
					this.router.navigate(['/not-found']);
				}
			});
		}
	}

	private loadPageTitleAndArticleContent() {
		if (!this.article) return;

		const lang = this.translateDynamicService.getLang();
		const translation = this.article.translations[lang];

		this.titleService.setTitle('Team Peps - ' + translation.title);
		this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(translation.content);
		this.publishSeo();
	}

	/**
	 * Balises et données structurées de l'article.
	 *
	 * La description est dérivée du contenu réel : une description propre à
	 * chaque article vaut bien mieux, en référencement, que celle du site
	 * répétée à l'identique sur toutes les pages.
	 */
	private publishSeo(): void {
		const article = this.article;
		if (!article) return;

		const lang = this.translateDynamicService.getLang();
		const translation = article.translations[lang];
		const path = `/news/${article.id}`;
		const pageUrl = this.seo.absoluteUrl(path);
		const image = this.seo.mediaUrl(article.imageKey || article.thumbnailImageKey);
		const description = SeoService.fromHtml(translation.content)
			|| this.translateService.instant('page.news-details.seo-description');

		this.seo.setPage({
			title: translation.title,
			description,
			path,
			image,
			type: 'article',
			publishedTime: article.createdAt ? new Date(article.createdAt).toISOString() : undefined,
		});

		this.jsonLd.set(
			NewsDetailsComponent.JSON_LD_ARTICLE,
			buildArticleJsonLd(article, { lang, siteUrl: env.siteUrl, pageUrl, image, description }),
		);
		this.jsonLd.set(
			NewsDetailsComponent.JSON_LD_BREADCRUMB,
			buildBreadcrumbJsonLd([
				{ name: 'Team Peps', url: this.seo.absoluteUrl('/') },
				{ name: this.translateService.instant('common.news'), url: this.seo.absoluteUrl('/news') },
				{ name: translation.title, url: pageUrl },
			]),
		);
	}
}
